import { Box, Grid2 as Grid } from "@mui/material";
import { SelectBox, TextField } from "../../common";
import { ChargeInformationRow, ModalForm } from "../../../types";
import { useValidateForm } from "../../../hooks";
import { editWashChargeSchema } from "../../../validations";
import { washTypesDropDownAtom } from "../../../atoms";
import { useAtom } from "jotai";
import { WASH_FREQUENCIES } from "../../../constants";
import { useState } from "react";

type Props = ModalForm<
  ChargeInformationRow,
  { washTypeId: number } & Omit<ChargeInformationRow, "washType" | "valueType">
>;

export const WashChargeForm = ({ id, defaultValues, handleEdit }: Props) => {
  const [washTypesDropDown] = useAtom(washTypesDropDownAtom);
  const { register, handleSubmit, errors } =
    useValidateForm(editWashChargeSchema);

  const existingFrequency = defaultValues?.frequency ?? "";

  const isCustomFrequencyValue =
    /^\d+days$/.test(existingFrequency) &&
    !WASH_FREQUENCIES.some((item) => item.value === existingFrequency);

  const existingCustomDays = isCustomFrequencyValue
    ? existingFrequency.replace("days", "")
    : "";

  const [isCustomFrequency, setIsCustomFrequency] = useState(
    isCustomFrequencyValue,
  );

  const handleFrequencyChange = (event: any) => {
    setIsCustomFrequency(event.target.value === "custom");
  };

  const handleEditWrapper = (data: {
    washRate: any;
    washTypeId: number;
    frequency: string;
    customFrequencyDays?: string;
    instructions: string;
  }) => {
    const washFrequency =
      data.frequency === "custom"
        ? `${data.customFrequencyDays}days`
        : data.frequency;

    const { customFrequencyDays, ...restData } = data;

    handleEdit({
      ...restData,
      washRate: Number(data.washRate),
      frequency: washFrequency,
      id: defaultValues ? defaultValues.id : 0,
    });
  };

  return (
    <Box
      onSubmit={handleSubmit(handleEditWrapper)}
      component={"form"}
      id={id}
      noValidate
      sx={{
        height: "100%",
      }}
    >
      <Grid
        container
        size={12}
        direction={"column"}
        gap={3}
        sx={{
          borderRadius: "10px",
          p: 3,
          mb: 2,
          width: "100%",
        }}
      >
        <Grid size={12}>
          {washTypesDropDown && (
            <SelectBox
              label="Wash Type"
              name="washTypeId"
              dropdown={washTypesDropDown}
              register={register}
              error={errors["washTypeId"]}
              defaultValue={defaultValues && defaultValues.id}
              valueAsNumber={true}
            />
          )}
        </Grid>
        <Grid size={12}>
          <TextField
            type={"number"}
            label="Wash Rate"
            name="washRate"
            register={register}
            error={errors["washRate"]}
            defaultValue={defaultValues && defaultValues.washRate}
          />
        </Grid>
        <Grid size={12}>
          <SelectBox
            label="Frequency"
            name="frequency"
            dropdown={WASH_FREQUENCIES}
            register={register}
            error={errors["frequency"]}
            defaultValue={isCustomFrequency ? "custom" : existingFrequency}
            onChange={handleFrequencyChange}
          />
        </Grid>
        {isCustomFrequency && (
          <Grid size={12}>
            <TextField
              type="number"
              label="Every"
              name="customFrequencyDays"
              register={register}
              error={errors["customFrequencyDays"]}
              defaultValue={existingCustomDays}
              slotProps={{
                input: {
                  endAdornment: "Days",
                },
              }}
            />
          </Grid>
        )}

        <Grid size={12}>
          <TextField
            label="Instructions"
            name="instructions"
            multiline
            rows={4}
            register={register}
            error={errors["instructions"]}
            defaultValue={defaultValues && defaultValues.instructions}
            maxLength={300}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
